import React, { useState } from 'react';
import type { AdminCourse, AdminCoursePayload } from '../../../../types/admin';

interface CourseFormModalProps {
  course: AdminCourse | null; // null = crea nuovo corso
  onClose: () => void;
  onSave: (payload: AdminCoursePayload) => Promise<void>;
}

const slugify = (title: string): string =>
  title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const CourseFormModal: React.FC<CourseFormModalProps> = ({
  course,
  onClose,
  onSave,
}) => {
  const isEdit = course !== null;

  const [id, setId] = useState(course?.id ?? '');
  const [title, setTitle] = useState(course?.title ?? '');
  const [subtitle, setSubtitle] = useState(course?.subtitle ?? '');
  const [description, setDescription] = useState(course?.description ?? '');
  const [imageUrl, setImageUrl] = useState(course?.imageUrl ?? '');
  const [price, setPrice] = useState(course?.price?.toString() ?? '');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const idError = !id.trim() ? "L'id del corso è obbligatorio." : '';
  const titleError = !title.trim() ? 'Il titolo è obbligatorio.' : '';
  const isValid = !idError && !titleError;

  const handleTitleChange = (value: string) => {
    setTitle(value);
    // Suggerisce l'id dal titolo solo in creazione, finché non viene toccato a mano
    if (!isEdit && !touched.id) {
      setId(slugify(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ id: true, title: true });
    if (!isValid) return;

    setSaving(true);
    setError('');
    try {
      await onSave({
        id: id.trim(),
        title: title.trim(),
        subtitle: subtitle.trim() || undefined,
        description: description.trim() || undefined,
        imageUrl: imageUrl.trim() || undefined,
        price: price.trim() ? Number(price) : undefined,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Errore durante il salvataggio.'
      );
      setSaving(false);
    }
  };

  return (
    <div className="acr-overlay" role="dialog" aria-modal="true">
      <div className="acr-dialog">
        <div className="acr-dialog__header">
          <h2 className="acr-dialog__title">
            {isEdit ? 'Modifica corso' : 'Nuovo corso'}
          </h2>
          <button
            className="acr-icon-btn"
            onClick={onClose}
            aria-label="Chiudi"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {error && (
          <div className="acr-error" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}

        <form className="acr-form" onSubmit={handleSubmit} noValidate>
          <label className="acr-label" htmlFor="acr-title">
            Titolo
          </label>
          <input
            id="acr-title"
            className="acr-input"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, title: true }))}
          />
          {touched.title && titleError && (
            <p className="acr-field-error">{titleError}</p>
          )}

          <label className="acr-label" htmlFor="acr-id">
            Id (slug, usato negli URL)
          </label>
          <input
            id="acr-id"
            className="acr-input"
            value={id}
            disabled={isEdit}
            onChange={(e) => setId(slugify(e.target.value))}
            onBlur={() => setTouched((t) => ({ ...t, id: true }))}
          />
          {touched.id && idError && (
            <p className="acr-field-error">{idError}</p>
          )}

          <label className="acr-label" htmlFor="acr-subtitle">
            Sottotitolo
          </label>
          <input
            id="acr-subtitle"
            className="acr-input"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
          />

          <label className="acr-label" htmlFor="acr-description">
            Descrizione
          </label>
          <textarea
            id="acr-description"
            className="acr-textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <label className="acr-label" htmlFor="acr-image">
            URL immagine
          </label>
          <input
            id="acr-image"
            className="acr-input"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />

          <label className="acr-label" htmlFor="acr-price">
            Prezzo (€)
          </label>
          <input
            id="acr-price"
            className="acr-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Vuoto = non acquistabile (coming soon)"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />

          <div className="acr-dialog__actions">
            <button
              type="button"
              className="acr-action-btn"
              onClick={onClose}
              disabled={saving}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="acr-action-btn acr-action-btn--primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="acr-spinner me-2" />
                  Salvataggio…
                </>
              ) : isEdit ? (
                'Salva modifiche'
              ) : (
                'Crea corso'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseFormModal;
